import { AppButton } from "@/components/teacher/AppButton";
import { LessonSlideGrid, LessonSlideGridSkeleton } from "@/components/teacher/LessonSlideGrid";
import { StatPill } from "@/components/teacher/StatPill";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { GetLessonBreadcrumbsDocument } from "@/generated/graphql";
import { format, useT } from "@/i18n";
import { useSlidesByLessonId } from "@/lib/hooks/slides/useSlidesByLessonId";
import { toPlayOrder } from "@/lib/slides/playOrder";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";

const SKELETON_COUNT = 8;

/** One lesson a teacher can assign from: every slide as the student will see it. */
export function TeacherLessonPage({ lessonId }: { lessonId: string }) {
  const navigate = useNavigate();
  const t = useT();

  const lessonQuery = useQuery(GetLessonBreadcrumbsDocument, {
    variables: { id: lessonId },
    fetchPolicy: "cache-first",
  });
  const { lessonSlideItems, lessonSlidesLoading, lessonSlidesError, refetchLessonSlides } =
    useSlidesByLessonId({ lessonId });

  const lessonName = lessonQuery.data?.lesson?.breadcrumbs?.at(-1)?.name;
  const slides = toPlayOrder(lessonSlideItems);

  return (
    <TeacherPage
      title={lessonName ?? t["lesson.fallbackName"]}
      actions={
        <AppButton tone="glass" onClick={() => navigate({ to: "/teacher/books" })}>
          {t["common.back"]}
        </AppButton>
      }
    >
      {lessonSlidesError && slides.length === 0 ? (
        <div className="app-card space-y-3 p-6 text-center">
          <p className="body text-app-muted">{t["lesson.slidesLoadError"]}</p>
          <AppButton tone="quiet" onClick={() => void refetchLessonSlides()}>
            {t["common.tryAgain"]}
          </AppButton>
        </div>
      ) : lessonSlidesLoading && slides.length === 0 ? (
        <LessonSlideGridSkeleton count={SKELETON_COUNT} />
      ) : slides.length === 0 ? (
        <p className="app-card body-sm p-6 text-app-muted">{t["lesson.empty"]}</p>
      ) : (
        <>
          <StatPill>
            {format(slides.length === 1 ? t["lesson.slideOne"] : t["lesson.slideOther"], {
              count: slides.length,
            })}
          </StatPill>

          <LessonSlideGrid
            items={slides.map((slide) => ({ slide }))}
            onPickSlide={(slideId) =>
              navigate({
                to: "/teacher/books/lessons/$lessonId/slides/$slideId",
                params: { lessonId, slideId },
              })
            }
          />
        </>
      )}
    </TeacherPage>
  );
}
